import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DataService } from 'src/app/data.service';

interface CheckoutSettings {
  test_mode: boolean;
  live_secret_key: string;
  live_publishable_key: string;
  live_webhook_secret: string;
  test_secret_key: string;
  test_publishable_key: string;
  test_webhook_secret: string;
  intezmeny?: string;
  szamlatombok?: SzamlatombOption[];
  default_proforma_tomb_id?: string;
  default_invoice_tomb_id?: string;
  default_proforma_tomb_name?: string;
  default_invoice_tomb_name?: string;
  has_live_secret_key?: boolean;
  has_live_webhook_secret?: boolean;
  has_test_secret_key?: boolean;
  has_test_webhook_secret?: boolean;
}

/**
 * OTP SimplePay beállítások. Külön táblából/végpontról jön (/api/simplepay/settings),
 * a Stripe beállításokat nem érinti. A `szolgaltato` dönti el, melyik fizetési gombot
 * kapják a szülők.
 */
interface SimplePaySettings {
  intezmeny?: string;
  aktiv: boolean;
  /** 'nincs' = a bankkártyás fizetés teljesen ki van kapcsolva. */
  szolgaltato: 'stripe' | 'simple' | 'mindketto' | 'nincs';
  /** Próbaüzem: a fizetés csak az adminisztrátoroknak jelenik meg. */
  probauzem: boolean;
  test_mode: boolean;
  live_merchant: string;
  test_merchant: string;
  live_secret_key: string;
  test_secret_key: string;
  live_secret_key_beallitva?: boolean;
  test_secret_key_beallitva?: boolean;
  penznem: string;
  base_url: string;
  ceg_nev: string;
  ceg_szekhely: string;
  webcim: string;
  ipn_url?: string;
  teszt_url?: string | null;
  back_url?: string;
}

interface SzamlatombOption {
  id: string;
  nev: string;
  elotag: string;
  formatum: number;
  intezmeny: string;
  def: number;
  proconn?: string;
}

@Component({
  selector: 'app-checkout-settings',
  templateUrl: './checkout-settings.component.html',
  styleUrls: ['./checkout-settings.component.scss'],
})
export class CheckoutSettingsComponent implements OnInit {
  loading = false;
  saving = false;
  settings: CheckoutSettings = this.emptySettings();
  sajatIntezmeny: any;

  // ---- OTP SimplePay ----
  simpleLoading = false;
  simpleSaving = false;
  simple: SimplePaySettings = this.emptySimple();

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    this.sajatIntezmeny = await this.data.getFromStorage('sajatIntezmeny');
    this.loadSettings();
    this.loadSimple();
  }

  /* ─────────────── OTP SimplePay beállítások ─────────────── */

  loadSimple() {
    this.simpleLoading = true;
    const intezmeny = encodeURIComponent(this.currentIntezmeny());
    this.http.get<SimplePaySettings>(`/api/simplepay/settings?intezmeny=${intezmeny}`).subscribe({
      next: response => {
        this.simple = this.normalizeSimple(response);
        this.simpleLoading = false;
      },
      error: error => {
        console.error('SimplePay beállítás betöltési hiba:', error);
        this.simpleLoading = false;
        this.data.sendToast('Nem sikerült betölteni a SimplePay beállításokat.', 'error');
      },
    });
  }

  saveSimple() {
    this.simpleSaving = true;
    this.http.put<any>('/api/simplepay/settings', {
      ...this.simple,
      intezmeny: this.currentIntezmeny(),
    }).subscribe({
      next: () => {
        this.simpleSaving = false;
        this.data.sendToast('A SimplePay beállítások mentve.', 'success');
        // Újratöltés, hogy a maszkolt kulcs-jelzők frissüljenek.
        this.loadSimple();
      },
      error: error => {
        console.error('SimplePay beállítás mentési hiba:', error);
        this.simpleSaving = false;
        this.data.sendToast('Nem sikerült menteni a SimplePay beállításokat.', 'error');
      },
    });
  }

  private emptySimple(): SimplePaySettings {
    return {
      aktiv: false,
      szolgaltato: 'stripe',
      probauzem: false,
      test_mode: true,
      live_merchant: '',
      test_merchant: '',
      live_secret_key: '',
      test_secret_key: '',
      penznem: 'HUF',
      base_url: '',
      ceg_nev: '',
      ceg_szekhely: '',
      webcim: '',
    };
  }

  private normalizeSimple(value: any): SimplePaySettings {
    return {
      ...this.emptySimple(),
      ...(value || {}),
      aktiv: !!value?.aktiv,
      test_mode: !!value?.test_mode,
      szolgaltato: ['stripe', 'simple', 'mindketto', 'nincs'].includes(value?.szolgaltato) ? value.szolgaltato : 'stripe',
      probauzem: !!value?.probauzem,
      // A kulcsokat a backend maszkolva adja vissza – a mezők üresen indulnak,
      // üresen hagyva pedig megmarad a korábbi érték.
      live_secret_key: '',
      test_secret_key: '',
    };
  }

  // A SimplePay csak akkor választható ki élesben, ha a szükséges adatok megvannak.
  simpleKeszEllenorzes(): string | null {
    // Kikapcsolt fizetésnél nincs mit hiányolni – ott a döntés a lényeg, nem a beállítás.
    if (this.simple.szolgaltato === 'nincs') return null;
    if (this.simple.szolgaltato === 'stripe') return null;
    const merchant = this.simple.test_mode ? this.simple.test_merchant : this.simple.live_merchant;
    const kulcsVan = this.simple.test_mode
      ? (this.simple.test_secret_key_beallitva || !!this.simple.test_secret_key)
      : (this.simple.live_secret_key_beallitva || !!this.simple.live_secret_key);
    if (!this.simple.aktiv) return 'A SimplePay ki van kapcsolva – a szülők a Stripe gombot látják.';
    if (!merchant) return `Hiányzik a ${this.simple.test_mode ? 'teszt' : 'éles'} MERCHANT azonosító.`;
    if (!kulcsVan) return `Hiányzik a ${this.simple.test_mode ? 'teszt' : 'éles'} SECRET_KEY.`;
    return null;
  }

  loadSettings() {
    this.loading = true;
    const intezmeny = encodeURIComponent(this.currentIntezmeny());
    this.http.get<CheckoutSettings>(`/api/nyilvantartas/checkout-settings?intezmeny=${intezmeny}`).subscribe({
      next: response => {
        this.settings = this.normalize(response);
        this.loading = false;
      },
      error: error => {
        console.error('Checkout beállítás betöltési hiba:', error);
        this.loading = false;
        this.data.sendToast('Nem sikerült betölteni a checkout beállításokat.', 'error');
      },
    });
  }

  saveSettings() {
    this.saving = true;
    this.http.put<CheckoutSettings>('/api/nyilvantartas/checkout-settings', {
      ...this.settings,
      intezmeny: this.currentIntezmeny(),
    }).subscribe({
      next: response => {
        this.settings = this.normalize(response);
        this.saving = false;
        this.data.sendToast('A checkout fizetés beállításai mentve.', 'success');
      },
      error: error => {
        console.error('Checkout beállítás mentési hiba:', error);
        this.saving = false;
        this.data.sendToast('Nem sikerült menteni a checkout beállításokat.', 'error');
      },
    });
  }

  private emptySettings(): CheckoutSettings {
    return {
      test_mode: false,
      live_secret_key: '',
      live_publishable_key: '',
      live_webhook_secret: '',
      test_secret_key: '',
      test_publishable_key: '',
      test_webhook_secret: '',
      szamlatombok: [],
      default_proforma_tomb_id: '',
      default_invoice_tomb_id: '',
      default_proforma_tomb_name: '',
      default_invoice_tomb_name: '',
    };
  }

  private normalize(value: any): CheckoutSettings {
    return {
      ...this.emptySettings(),
      ...(value || {}),
      test_mode: !!value?.test_mode,
      szamlatombok: Array.isArray(value?.szamlatombok) ? value.szamlatombok : [],
    };
  }

  tombLabel(item: SzamlatombOption) {
    return [item.elotag, item.nev].filter(Boolean).join(' - ');
  }

  private currentIntezmeny() {
    return String(this.sajatIntezmeny?.intezmenyId || '203508/001').replace(/^\/+|\/+$/g, '');
  }
}
